import { TFunction } from "i18next";
import { FilterContexType } from "../../../components/layout/ViewHeader";
import { FilterDefinition, FiltersRegistry } from "../types";

// Get filters by registry group
// Takes a registry group id and returns the resolved FilterDefinition list
const getFiltersByGroup = (
	registry: FiltersRegistry | null,
	group: keyof FiltersRegistry["groups"]
): FilterDefinition[] => {
	if (!registry) return [];

	// map ids -> actual filters and remove invalid ones
	return registry.groups[group].map((id) => registry.byId[id]).filter(Boolean);
};

// Filter by visibility flags
// Keeps only filters that match at least one requested visibility
const applyVisibility = (filters: FilterDefinition[], visibility?: FilterContexType): FilterDefinition[] => {
	if (!visibility?.length) return filters;

	return filters.filter((filter) => filter.visibility?.some((v) => visibility.includes(v)));
};

// Filter by search string
// Simple case-insensitive match on filter name
const applySearch = (filters: FilterDefinition[], search?: string): FilterDefinition[] => {
	if (!search?.trim()) return filters;

	const normalizedSearch = search.toLowerCase().trim();

	return filters.filter((filter) => filter.name.toLowerCase().includes(normalizedSearch));
};

// Sort filters for UI display
// Favorites first, then alphabetical order
const sortFilters = (filters: FilterDefinition[]): FilterDefinition[] => {
	return [...filters].sort((a, b) => {
		const favoriteA = Number(a.favorite ?? false);
		const favoriteB = Number(b.favorite ?? false);

		// favorites first
		if (favoriteA !== favoriteB) {
			return favoriteB - favoriteA;
		}

		// alphabetical fallback
		return a.name.localeCompare(b.name);
	});
};

// Available filters menu section
// Applies visibility + search + sorting
export const getMenuAvailableFilters = (
	registry: FiltersRegistry | null,
	params?: {
		visibility?: FilterContexType;
		search?: string;
	}
): FilterDefinition[] => {
	const filters = getFiltersByGroup(registry, "available");

	return sortFilters(applySearch(applyVisibility(filters, params?.visibility), params?.search));
};

// Saved simple filters menu section
export const getMenuSavedFilters = (
	registry: FiltersRegistry | null,
	params?: {
		visibility?: FilterContexType;
		search?: string;
	}
): FilterDefinition[] => {
	const filters = getFiltersByGroup(registry, "saved");

	return sortFilters(applySearch(applyVisibility(filters, params?.visibility), params?.search));
};

// Saved configurations menu section
export const getMenuConfigurations = (
	registry: FiltersRegistry | null,
	params?: {
		visibility?: FilterContexType;
		search?: string;
	}
): FilterDefinition[] => {
	const filters = getFiltersByGroup(registry, "configurations");

	return sortFilters(applySearch(applyVisibility(filters, params?.visibility), params?.search));
};

// Build menu sections for UI
// Combines all groups into structured sections with translated titles
export const getMenuSections = (
	registry: FiltersRegistry | null,
	t: TFunction,
	params?: {
		visibility?: FilterContexType;
		search?: string;
	}
) => {
	return [
		{
			id: "saved",
			title: t("saved filters"),
			items: getMenuSavedFilters(registry, params),
		},
		{
			id: "available",
			title: t("other filters"),
			items: getMenuAvailableFilters(registry, params),
		},
		{
			id: "configurations",
			title: t("configurations"),
			items: getMenuConfigurations(registry, params),
		},
	];
};
